import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Button,
  Typography,
  Grid,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  Tooltip
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';
import DeleteIcon from '@mui/icons-material/Delete';
import {
  uploadListingImages,
  getListingById,
  updateListingImageOrder,
  deleteListingImage
} from './services/listingApi';

const ImageUpload = () => {
  const [listingId, setListingId] = useState('');
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previews, setPreviews] = useState([]);
  const [cardDetails, setCardDetails] = useState(null);

  const [imageUrls, setImageUrls] = useState([]); // saved URLs
  const [editorOpen, setEditorOpen] = useState(false);

  const isUploadingRef = useRef(false);

  // Warn before unload during upload/selection
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isUploadingRef.current || selectedFiles.length > 0) {
        e.preventDefault();
        e.returnValue = 'You have an upload in progress. Are you sure you want to leave?';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [selectedFiles]);

  // Normalize image_urls (array or JSON string)
  const normalizeImageUrls = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    if (typeof raw === 'string') {
      try {
        const parsed = JSON.parse(raw);
        return Array.isArray(parsed) ? parsed : [];
      } catch {
        return [];
      }
    }
    return [];
  };

  // Fetch listing by ID
  useEffect(() => {
    const fetchCardDetails = async () => {
      if (!listingId) {
        setCardDetails(null);
        setImageUrls([]);
        return;
      }
      try {
        const listing = await getListingById(listingId);
        if (listing) {
          setCardDetails({
            card_name: listing.card_name,
            rarity: listing.rarity,
            card_set: listing.card_set
          });
          setImageUrls(normalizeImageUrls(listing.image_urls));
        } else {
          setCardDetails(null);
          setImageUrls([]);
        }
      } catch (err) {
        console.error('Error fetching listing:', err);
        setCardDetails(null);
        setImageUrls([]);
      }
    };
    fetchCardDetails();
  }, [listingId]);

  // Select files (cap to 12 total new selections; saved count is separate)
  const handleFilesSelect = (event) => {
    const newFiles = Array.from(event.target.files || []);
    const combinedFiles = [...selectedFiles, ...newFiles].slice(0, 12);
    setSelectedFiles(combinedFiles);
    setPreviews(combinedFiles.map(f => URL.createObjectURL(f)));
  };

  const handleDeleteNew = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
    setPreviews(newFiles.map(f => URL.createObjectURL(f)));
  };

  const handleUpload = async () => {
    if (!listingId || selectedFiles.length === 0) {
      alert('Listing ID and at least one image required.');
      return;
    }
    const formData = new FormData();
    formData.append('listing_id', listingId);
    selectedFiles.forEach(file => formData.append('images[]', file));

    try {
      isUploadingRef.current = true;
      const response = await uploadListingImages(formData);
      if (response?.image_urls) setImageUrls(response.image_urls);
      alert(`Images uploaded successfully (${response?.image_count ?? 'updated'} total).`);
      setSelectedFiles([]);
      setPreviews([]);
    } catch (e) {
      alert(`Error uploading images: ${e.message}`);
    } finally {
      isUploadingRef.current = false;
    }
  };

  // Persisted reorder/delete
  const persistOrder = async (urls) => {
    if (!listingId) return;
    try {
      const res = await updateListingImageOrder(listingId, urls);
      if (res?.image_urls) setImageUrls(res.image_urls);
    } catch (e) {
      console.error('Failed to save image order', e);
      alert('Failed to save image order.');
    }
  };

  const moveImage = (index, direction) => {
    const newIndex = index + direction;
    if (newIndex < 0 || newIndex >= imageUrls.length) return;
    const arr = [...imageUrls];
    const [item] = arr.splice(index, 1);
    arr.splice(newIndex, 0, item);
    setImageUrls(arr);
    persistOrder(arr);
  };

  const deleteSavedImage = async (url) => {
    if (!listingId) return;
    try {
      const res = await deleteListingImage(listingId, url);
      if (res?.image_urls) setImageUrls(res.image_urls);
    } catch (e) {
      console.error('Failed to delete image', e);
      alert('Failed to delete image.');
    }
  };

  return (
    <Box sx={{ p: 2 }}>
      <Typography variant="h6">Upload Listing Images</Typography>

      <TextField
        label="Listing ID"
        variant="outlined"
        fullWidth
        margin="normal"
        value={listingId}
        onChange={(e) => setListingId(e.target.value)}
      />

      {cardDetails && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          <strong>Card:</strong> {cardDetails.card_name} � {cardDetails.rarity} � {cardDetails.card_set}
        </Typography>
      )}

      {listingId && (
        <Typography variant="body2" sx={{ mt: 1 }}>
          Images on listing: <strong>{imageUrls.length}</strong> / 12
        </Typography>
      )}

      <Box sx={{ mt: 1 }}>
        <Button variant="contained" component="label" disabled={imageUrls.length >= 12}>
          Add Photos
          <input
            type="file"
            hidden
            multiple
            accept="image/*"
            capture="environment"
            onChange={handleFilesSelect}
          />
        </Button>

        <Button
          variant="outlined"
          sx={{ ml: 2 }}
          onClick={handleUpload}
          disabled={!selectedFiles.length}
        >
          Upload
        </Button>

        <Button
          variant="text"
          sx={{ ml: 2 }}
          disabled={!imageUrls.length}
          onClick={() => setEditorOpen(true)}
        >
          Edit Images
        </Button>
      </Box>

      {/* New (not yet uploaded) previews */}
      <Grid container spacing={2} sx={{ mt: 2 }}>
        {previews.map((src, idx) => (
          <Grid item xs={4} sm={2} key={`new-${idx}`}>
            <Paper elevation={2} sx={{ position: 'relative' }}>
              <img src={src} alt={`Preview ${idx}`} style={{ width: '100%', height: 'auto', display: 'block' }} />
              <IconButton
                size="small"
                onClick={() => handleDeleteNew(idx)}
                sx={{ position: 'absolute', top: 0, right: 0 }}
              >
                <CloseIcon fontSize="small" />
              </IconButton>
            </Paper>
          </Grid>
        ))}
      </Grid>

      {/* Editor dialog: reorder + delete saved images */}
      <Dialog open={editorOpen} onClose={() => setEditorOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Images (reorder & delete) � {imageUrls.length} total</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            {imageUrls.map((src, idx) => (
              <Grid item xs={6} sm={4} md={3} key={`saved-${idx}`}>
                <Paper elevation={1} sx={{ position: 'relative', p: 0.5 }}>
                  <img src={src} alt={`Saved ${idx}`} style={{ width: '100%', height: 'auto', display: 'block' }} />
                  <Box sx={{ position: 'absolute', top: 4, left: 4, display: 'flex', gap: 0.5 }}>
                    <Tooltip title="Move left">
                      <span>
                        <IconButton size="small" onClick={() => moveImage(idx, -1)} disabled={idx === 0}>
                          <ArrowBackIosNewIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                    <Tooltip title="Move right">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => moveImage(idx, +1)}
                          disabled={idx === imageUrls.length - 1}
                        >
                          <ArrowForwardIosIcon fontSize="inherit" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                  <Box sx={{ position: 'absolute', top: 4, right: 4 }}>
                    <Tooltip title="Delete">
                      <IconButton size="small" onClick={() => deleteSavedImage(src)}>
                        <DeleteIcon fontSize="inherit" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </DialogContent>
      </Dialog>
    </Box>
  );
};

export default ImageUpload;
